import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { SocialMediaService } from "../../../services/SocialMediaService";
import { ISocialMediaItem } from "../../../models/Interfaces";
import Loading from "../../components/common/Loading";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import FilteredTable from "../../components/common/FilteredTable";
import AppModal from "../../components/common/AppModal";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import { Core, formValidation, notEmpty } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { useConfirm } from "../../../config/ConfirmProvider";
import styles from "./socialMedia.module.scss";

const SocialMedia = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [socialMediaItems, setSocialMediaItems] = useState<Array<ISocialMediaItem>>([]);
  const [selectedItem, setSelectedItem] = useState<ISocialMediaItem | null>(null);

  const [showDetail, setShowDetail] = useState<boolean>(false);

  const smServiceRef = useRef<SocialMediaService | null>(null);
  const smFormRef = useRef<HTMLFormElement | null>(null);
  const smFvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const columns: Column<ISocialMediaItem>[] = useMemo(() => [
    {
      Header: "Name",
      accessor: "Name"
    },
    {
      id: "Preview",
      Header: "Preview",
      Cell: ({ row }: CellProps<ISocialMediaItem>) => {
        const item = row.original;
        return item.ImageUrl ? (
          <div className={styles.previewCell}>
            <img src={item.ImageUrl} alt={item.AltText || item.Name} className={styles.previewImage} />
          </div>
        ) : item.IconClass ? (
          <div className={styles.previewCell}>
            <span className={`${item.IconClass} ${styles.previewIcon}`} aria-hidden="true"></span>
          </div>
        ) : (
          <span className={styles.previewFallback}>None</span>
        );
      }
    },
    {
      Header: "Url",
      id: "Url",
      Cell: ({ row }: CellProps<ISocialMediaItem>) => (
        <span className={styles.urlValue}>{row.original.Url?.toLowerCase() ?? ""}</span>
      )
    },
    {
      Header: "Alt Text",
      accessor: "AltText"
    },
    {
      Header: "Icon",
      id: "Icon",
      Cell: ({ row }: CellProps<ISocialMediaItem>) => {
        return row.original.IconClass ? <span className={styles.iconValue}>{row.original.IconClass?.toLowerCase()}</span> : <span className={styles.previewFallback}>None</span>;
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ISocialMediaItem>) => (
        <div className="user-result-actions">
          <Button variant="light" onClick={() => editItem(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit social media item">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteItem(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete social media item">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], []);

  const createValidator = (): void => {
    if (smFormRef.current && !smFvRef.current) {
      smFvRef.current = createFormValidation(smFormRef.current, {
        fields: {
          Name: {
            validators: {
              notEmpty: { message: "social media name is required" }
            }
          },
          Url: {
            validators: {
              notEmpty: { message: "link to social media page is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger()
        }
      });
    }
  };

  const getSocialMedia = async (): Promise<void> => {
    if (smServiceRef.current) {
      const res = await smServiceRef.current.getSocialMediaItems();

      if (res?.length > 0) {
        setSocialMediaItems(res);
      }
    }
  };

  const addItem = (): void => {
    if (smServiceRef.current) {
      const item = smServiceRef.current.newSocialMediaItem();

      if (item) {
        setSelectedItem(item);
        setShowDetail(true);
      }
    }
  };

  const editItem = (item: ISocialMediaItem): void => {
    if (item) {
      setSelectedItem(item);
      setShowDetail(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => closeDetail());

  const deleteItem = async (item: ISocialMediaItem): Promise<void> => {
    if (smServiceRef.current && item) {
      const confirmed = await confirmation.requestConfirmation(`Delete ${item.Name}? <b>This action cannot be undone.</b>`, "Delete Social Media Item");

      if (confirmed) {
        const res = await smServiceRef.current.deleteSocialMediaItem(item.Id);
        if (res) {
          setSocialMediaItems(prev => prev.filter(sm => sm.Id !== item.Id));
          closeDeletedSelection(item.Id);
        }
      }
    }
  };

  const saveItem = async (): Promise<void> => {
    if (smServiceRef.current && selectedItem && smFvRef.current) {
      const validres = await smFvRef.current.validate();

      if (validres === "Valid") {
        const res = await smServiceRef.current.saveSocialMediaItem(selectedItem);

        if (res) {
          const existing = socialMediaItems.find(sm => sm.Id === res.Id);

          if (existing) {
            setSocialMediaItems(prev => prev.map(sm => sm.Id === res.Id ? res : sm));
          } else {
            setSocialMediaItems(prev => [
              ...prev,
              res
            ]);
          }
        }
        closeDetail();
      }
    }
  };

  const closeDetail = (): void => {
    setSelectedItem(null);
    setShowDetail(false);
  };

  const handlePropertyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedItem) {
      const { name, value } = event.target;
      updateProperty(name, value);
    }
  };

  const updateProperty = (name: string, value: any): void => {
    if (selectedItem && name) {
      if (name) {
        setSelectedItem(prev => ({
          ...prev as ISocialMediaItem,
          [name]: value
        }));
      }
    }
  };

  useEffect(() => {
    if (smFormRef.current) {
      createValidator();
    } else {
      smFvRef.current = null;
    }
  }, [smFormRef, showDetail]);

  useEffect(() => {
    const getData = async () => {
      try {
        await getSocialMedia();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    if (!smServiceRef.current) {
      smServiceRef.current = new SocialMediaService();
    }
    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading social media items..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page} id="social-media-items">
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">Channel Library</h2>
              </div>
            </div>
            <div className="app-list-section__body">
              <FilteredTable<ISocialMediaItem>
                columns={columns}
                data={socialMediaItems}
                noDataMessage="No social media channels configured yet. Use the add button above to create the first one."
                showAddButton={true}
                addButtonText="Add social media channel"
                addButtonHandler={addItem}
                filterPlaceHolder="filter channels..."
              />
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedItem && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="lg"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Social Media Item"
          onDataAction={saveItem}>
          <Form id="social-media-form" ref={smFormRef}>
            <Row>
              <Col md={12}>
                <FormLabel>Name</FormLabel>
                <FormControl
                  id="Name"
                  name="Name"
                  type="text"
                  value={selectedItem.Name}
                  onChange={handlePropertyChange}
                  placeholder="social media service name..."
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Link URL</FormLabel>
                <FormControl
                  id="Url"
                  name="Url"
                  type="text"
                  value={selectedItem.Url}
                  onChange={handlePropertyChange}
                  placeholder="URL to your social media service..."
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Link Image/Icon</FormLabel>
                <SiteMediaPicker
                  existingUrl={selectedItem.ImageUrl ?? ""}
                  onMediaSelect={(url) => updateProperty("ImageUrl", url)}
                  onMediaClear={() => updateProperty("ImageUrl", "")}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Image Alt Text</FormLabel>
                <FormControl
                  id="AltText"
                  name="AltText"
                  type="text"
                  value={selectedItem.AltText}
                  onChange={handlePropertyChange}
                  placeholder="text to display if image unavailable..."
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Icon Class (from custom css or FontAwesome)</FormLabel>
                <FormControl
                  id="IconClass"
                  name="IconClass"
                  type="text"
                  value={selectedItem.IconClass}
                  onChange={handlePropertyChange}
                  placeholder="icon class from stylesheet or font awesome..."
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
}

export default SocialMedia;
